import React from 'react';
import { ExternalLink, ArrowRight, Sparkles, Globe, TrendingUp } from 'lucide-react';

const CaseStudiesSection = () => {
  const caseStudies = [
    {
      id: 1,
      title: 'The Dolorosa',
      url: 'thedolorosa.com',
      subtitle: 'Complete Platform Migration & SEO Optimization',
      description: 'Transformed a legacy WordPress site into a modern, high-performance web application with comprehensive SEO/SEM strategy.',
      tags: ['WordPress Migration', 'Webflow', 'v0 Development', 'SEO/SEM'],
      journey: [
        {
          phase: 'Legacy WordPress',
          description: 'Outdated infrastructure with performance issues and limited scalability'
        },
        {
          phase: 'Webflow Migration',
          description: 'Modernized design and improved content management capabilities'
        },
        {
          phase: 'v0 Dynamic Site',
          description: 'Full custom development with dynamic features and SEO/SEM optimization'
        }
      ],
      results: [
        { metric: 'Platform Evolution', value: 'WordPress → Webflow → v0' },
        { metric: 'SEO Optimization', value: 'Complete Implementation' },
        { metric: 'Performance', value: 'Significantly Enhanced' },
        { metric: 'Management', value: 'Fully Dynamic & Managed' }
      ]
    },
    {
      id: 2,
      title: 'Back Your Badge',
      url: 'backyourbadge.us',
      subtitle: 'First Responder Support Platform',
      description: 'Built a modern web platform for Bryan Andrews, a veteran LA County firefighter, to support and honor first responders through community engagement.',
      tags: ['v0 Development', 'Community Platform', 'Custom Build'],
      journey: [
        {
          phase: 'Discovery & Planning',
          description: 'Collaborated with Bryan to understand the mission and community needs'
        },
        {
          phase: 'Design & Development',
          description: 'Built custom platform using v0 with focus on accessibility and engagement'
        },
        {
          phase: 'Launch & Support',
          description: 'Deployed platform to support first responders and their communities'
        }
      ],
      results: [
        { metric: 'Development', value: 'Custom v0 Build' },
        { metric: 'Purpose', value: 'First Responder Support' },
        { metric: 'Client', value: 'LA County Firefighter Veteran' },
        { metric: 'Community', value: 'Engagement Platform' }
      ]
    }
  ];

  return (
    <div className="py-16 px-4 bg-brand-dark text-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4 font-serif">
            Case Studies
          </h2>
          <p className="text-xl max-w-3xl mx-auto text-gray-300">
            Real transformations: From legacy systems to modern, optimized web experiences
          </p>
        </div>

        {/* Case Studies */}
        <div className="space-y-12">
          {caseStudies.map((study) => (
            <div
              key={study.id}
              className="bg-black/40 backdrop-blur-sm rounded-lg border border-coral-900/50 shadow-lg overflow-hidden"
            >
              {/* Header */}
              <div className="p-8 border-b border-coral-900/50">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="text-3xl font-semibold mb-2 font-serif text-white flex items-center gap-2">
                      {study.title}
                      <Sparkles className="h-6 w-6 text-coral-400" />
                    </h3>
                    <p className="text-coral-400 text-lg mb-2">{study.subtitle}</p>
                    <a
                      href={`https://${study.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-coral-400 transition-colors inline-flex items-center gap-2 group"
                    >
                      <Globe className="h-4 w-4" />
                      {study.url}
                      <ExternalLink className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {study.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-coral-500/20 text-coral-400 rounded-full text-sm font-medium border border-coral-500/30"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <p className="text-gray-300 mt-4">
                  {study.description}
                </p>
              </div>

              {/* Migration Journey */}
              <div className="p-8 border-b border-coral-900/50">
                <h4 className="text-xl font-semibold mb-6 font-serif text-white flex items-center gap-2">
                  <ArrowRight className="h-5 w-5 text-coral-400" />
                  Migration Journey
                </h4>
                <div className="grid md:grid-cols-3 gap-6">
                  {study.journey.map((step, idx) => (
                    <div
                      key={idx}
                      className="bg-black/30 rounded-lg p-6 border border-coral-900/30"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex items-center justify-center w-8 h-8 bg-coral-500/20 rounded-full text-coral-400 font-bold text-sm border border-coral-500/30">
                          {idx + 1}
                        </div>
                        <h5 className="font-semibold text-white">{step.phase}</h5>
                      </div>
                      <p className="text-sm text-gray-300">{step.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div className="p-8">
                <h4 className="text-xl font-semibold mb-6 font-serif text-white flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-coral-400" />
                  Key Results
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {study.results.map((result, idx) => (
                    <div
                      key={idx}
                      className="bg-black/30 rounded-lg p-4 border border-coral-900/30"
                    >
                      <div className="text-sm text-gray-400 mb-1">{result.metric}</div>
                      <div className="text-lg font-semibold text-coral-400">{result.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center bg-black/40 backdrop-blur-sm rounded-lg p-8 border border-coral-900/50">
          <h3 className="text-2xl font-semibold mb-3 font-serif text-white">
            Ready to Transform Your Digital Presence?
          </h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Whether you're dealing with a legacy system or looking to optimize your current platform,
            we can help you achieve your goals with modern, scalable solutions.
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                const offset = 80;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - offset;
                window.scrollTo({
                  top: offsetPosition,
                  behavior: 'smooth'
                });
              }
            }}
            className="bg-coral-500 hover:bg-coral-600 text-white font-medium px-8 py-3 rounded-full transition-colors duration-200 inline-flex items-center gap-2"
          >
            Start Your Project
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CaseStudiesSection;
